<script lang="ts">
  import type { Snippet } from 'svelte';

  // Centered overlay built on the native <dialog> element so the browser
  // handles focus trap, ESC key, and backdrop interception for free.
  // Parent controls `open` via two-way bind; Modal calls `showModal()` /
  // `close()` on the underlying dialog node when `open` flips.
  //
  // Backdrop click closes — we detect it by seeing the `dialog` itself as
  // the click target (children stop bubbling at the inner content wrapper).

  interface Props {
    open?: boolean;
    title?: string;
    /** Cap the modal width. Default 'md'. */
    size?: 'sm' | 'md' | 'lg';
    onClose?: () => void;
    children: Snippet;
    /** Optional footer (action buttons). Rendered in a distinct row below body. */
    footer?: Snippet;
  }

  let {
    open = $bindable(false),
    title,
    size = 'md',
    onClose,
    children,
    footer,
  }: Props = $props();

  let dialog = $state<HTMLDialogElement | null>(null);

  $effect(() => {
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  });

  function handleClose(): void {
    open = false;
    onClose?.();
  }

  function handleBackdropClick(event: MouseEvent): void {
    if (event.target === dialog) handleClose();
  }
</script>

<dialog
  bind:this={dialog}
  class="ui-modal ui-modal--{size}"
  onclose={handleClose}
  onclick={handleBackdropClick}
>
  <div class="ui-modal__inner">
    {#if title}
      <header class="ui-modal__header">
        <h2 class="ui-modal__title">{title}</h2>
        <button
          class="ui-modal__close"
          type="button"
          aria-label="Close"
          onclick={handleClose}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="6" y1="18" x2="18" y2="6" />
          </svg>
        </button>
      </header>
    {/if}
    <div class="ui-modal__body">{@render children()}</div>
    {#if footer}
      <footer class="ui-modal__footer">{@render footer()}</footer>
    {/if}
  </div>
</dialog>
